import { useMemo, useRef } from "react";

import { useRouter } from "next/router";

import { t, Trans } from "@lingui/macro";
import { Alert, Space, Tag, Typography } from "antd";
import { PageProps } from "layout";
import { isEmpty, orderBy } from "lodash";
import useSWR from "swr";

import AppLayout from "layout/app";

import Questions from "components/forms-view/questions";
import Responses from "components/forms-view/responses";
import UserGroupPermissions from "components/forms-view/user-group-permissions";
import UserPermissions from "components/forms-view/user-permissions";
import TabLayout from "components/tabs";

import { useApplicationContext } from "contexts/app";

import { AdditionalField, Form as NavirecForm, FormQuestion } from "types";

import { DraggableQuestion, getFormStateColor } from "utils/navirec-forms";

export const formStateLabel = {
  active: <Trans>Active</Trans>,
  draft: <Trans>Draft</Trans>,
  archived: <Trans>Archived</Trans>,
  submitted: <Trans>Submitted</Trans>,
};

const FormView: PageProps = () => {
  const { session, configuration } = useApplicationContext();
  const router = useRouter();

  const { id } = router.query;

  const createNew = id === "new";

  const { data: permissions } = useSWR(createNew || isEmpty(id) ? null : `/forms/${id}/effective_permissions/`);

  const { canManage, canAccess, canUpdateState, canViewResponses } = useMemo(() => {
    if (permissions) {
      return {
        canAccess: permissions.includes("can_access"),
        canViewResponses: permissions.includes("can_view_responses"),
        canSubmitResponses: permissions.includes("can_submit_responses"),
        canManage: permissions.includes("can_manage"),
        canUpdateState:
          permissions.includes("can_manage") || !!configuration?.account_permissions?.includes("can_manage_forms"),
      };
    }
    return {
      canUpdateState: true,
      canAccess: true,
      canViewResponses: true,
      canSubmitResponses: true,
      canManage: true,
    };
  }, [permissions]);

  const {
    data: formData,
    error: formError,
    mutate: refreshForm,
  } = useSWR<NavirecForm | null>(createNew || isEmpty(id) ? null : `/forms/${id}/`);

  const {
    data: questions,
    error: questionsError,
    mutate: mutateQuestions,
  } = useSWR<FormQuestion[] | null>(createNew || isEmpty(id) ? null : `/form_questions/?form=${id}&active=true`);

  const {
    data: additionalFields,
    error: additionalFieldsError,
    mutate: refreshAdditionalFields,
  } = useSWR<AdditionalField[]>(
    `/additional_fields/?account=${session?.accountId}&available__contains=vehicles&ordering=name`
  );

  const formQuestions = useMemo(() => (questions ? orderBy(questions, ["sort_order", "id"]) : []), [questions]);
  const questionsRef = useRef<null | DraggableQuestion[]>([]);

  const loadingFormData =
    !additionalFields && !additionalFieldsError && !formData && !formError && !formQuestions && !questionsError;

  const views = useMemo(() => {
    const result = [];

    if (canAccess) {
      result.push({
        key: "overview",
        label: t`Form overview`,
        children: (
          <Questions
            resetQuestions={() => {
              mutateQuestions(null);
            }}
            canUpdateState={canUpdateState as boolean}
            canEdit={canManage}
            refreshAdditionalFields={refreshAdditionalFields}
            formData={formData}
            refreshForm={refreshForm}
            loadingFormData={loadingFormData}
            formQuestions={formQuestions || []}
            additionalFields={additionalFields || []}
            savedQuestions={questionsRef}
          />
        ),
      });
    }

    if (formData) {
      if (canAccess || canViewResponses) {
        result.push({
          key: "responses",
          label: t`Responses`,
          children: <Responses formQuestions={formQuestions || []} formId={(formData as any)?.id || ""} />,
        });
      }
      result.push({
        key: "user_permissions",
        label: t`User permissions`,
        children: <UserPermissions />,
      });
      result.push({
        key: "user_group_permissions",
        label: t`User group permissions`,
        children: <UserGroupPermissions />,
      });
    }

    return result;
  }, [additionalFields, formData, canAccess, canViewResponses, canManage, formQuestions, canUpdateState]);

  return (
    <AppLayout
      autoScroll={false}
      fullscreen={false}
      pageTitle={t`Forms`}
      label={
        <Space size={16}>
          <Typography.Text>{formData?.name || t`New form`}</Typography.Text>
          {formData?.state && <Tag color={getFormStateColor(formData.state)}>{formStateLabel[formData.state]}</Tag>}
        </Space>
      }
    >
      {canAccess ? (
        <TabLayout views={views} />
      ) : (
        <Alert type="warning" message={t`You do not have permission for this form`} />
      )}
    </AppLayout>
  );
};

export default FormView;

FormView.Layout = "Private";
